import { useMemo, useState, useEffect } from "react";
import type { Decision, LogEventBase, RunSummary } from "../lib/types";
import { type Source, explainEmptySource } from "../lib/sources";
import { Mark } from "./Mark";

type Props = {
  run: RunSummary;
  source: Source | null;
  query: string;
  // When the top summary chip is active (e.g. "block"), the user expects
  // the bottom packages-table chip-filter to also default to "block" so
  // they see the relevant packages immediately. Empty string = no top
  // filter. Passed down; PackagesColumn keeps its own state but re-syncs
  // whenever this prop changes. User can override locally.
  initialActionFilter?: string;
};

type SortKey = "package" | "versions" | "action" | "why" | "source";
type SortDir = "asc" | "desc" | null;
const ACTION_VALUES: Decision["action"][] = ["block", "review", "none", "skip", "error", "filtered", "unknown"];

// Column 3 of the 3-column UI: the package list for the currently
// selected source. Most of the rendering is lifted from the old
// RunDetail.DecisionsSection — action chips, sortable columns, signals
// column, source links — so the polish is preserved. Empty state is
// special-cased: a blog post that returned 0 packages should explain why
// (parse error, truncated, unsupported ecosystem, classifier verdict
// other than compromise_alert).
export function PackagesColumn({ run, source, query, initialActionFilter }: Props) {
  if (!source) {
    return (
      <main className="packages packages--empty">
        <div className="packages__empty-msg">
          Select a source from the middle column to see its packages.
        </div>
      </main>
    );
  }

  return (
    <main className="packages">
      <PackagesHeader source={source} />
      <DecisionsTable
        decisions={source.decisions}
        query={query}
        initialActionFilter={initialActionFilter}
      />
      {source.decisions.length === 0 && <EmptyExplanation source={source} />}
      {source.kind === "blog-post" && <BlogPostExtras source={source} run={run} query={query} />}
    </main>
  );
}

function PackagesHeader({ source }: { source: Source }) {
  return (
    <div className="packages__head">
      <h2>
        {source.title} <span className="packages__count">({source.decisionCount} pkg{source.decisionCount === 1 ? "" : "s"})</span>
      </h2>
      {source.kind === "blog-post" && source.classifierCategory && (
        <div className="packages__meta">
          <span className={`tag tag--${source.classifierCategory}`}>
            classifier: {source.classifierCategory.replace(/_/g, " ")}
          </span>
          {source.extractedCount !== null && (
            <span className={`tag tag--extract${source.extractedCount === 0 ? "-zero" : ""}`}>
              extractor: {source.extractedCount} pkg{source.extractedCount === 1 ? "" : "s"}
            </span>
          )}
          {source.url && (
            <a className="link" href={source.url} target="_blank" rel="noreferrer">
              open post ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyExplanation({ source }: { source: Source }) {
  const reason = explainEmptySource(source);
  if (!reason) {
    return (
      <p className="packages__empty-msg">No packages from this source in this run.</p>
    );
  }
  return (
    <div className="packages__empty-box">
      <h3>Why no packages?</h3>
      <p>{reason}</p>
    </div>
  );
}

// Reused from RunDetail — same shape, same chips/sorting behavior.
function DecisionsTable({
  decisions,
  query,
  initialActionFilter,
}: {
  decisions: Decision[];
  query: string;
  initialActionFilter?: string;
}) {
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const d of decisions) c[d.action] = (c[d.action] || 0) + 1;
    return c;
  }, [decisions]);
  const presentActions = useMemo(
    () => ACTION_VALUES.filter((a) => (counts[a] || 0) > 0),
    [counts],
  );
  // Local state — initialized from the top-summary filter, but the user
  // can override locally (click "all" or a different chip) without
  // affecting the URL or other columns.
  const [activeFilter, setActiveFilter] = useState<string | null>(initialActionFilter || null);
  // Re-sync whenever the top filter changes. Each click on a top chip
  // pushes its value down here as the new active filter; clearing the
  // top filter ("") resets the bottom to "all".
  useEffect(() => {
    setActiveFilter(initialActionFilter || null);
  }, [initialActionFilter]);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const filtered = useMemo(
    () => (activeFilter ? decisions.filter((d) => d.action === activeFilter) : decisions),
    [decisions, activeFilter],
  );
  const sorted = useMemo(() => {
    if (!sortKey || !sortDir) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => sortValue(a, sortKey).localeCompare(sortValue(b, sortKey)) * dir);
  }, [filtered, sortKey, sortDir]);

  function clickHeader(k: SortKey) {
    if (sortKey !== k) {
      setSortKey(k);
      setSortDir("asc");
    } else if (sortDir === "asc") setSortDir("desc");
    else if (sortDir === "desc") {
      setSortKey(null);
      setSortDir(null);
    } else setSortDir("asc");
  }

  if (decisions.length === 0) return null;

  return (
    <>
      <div className="action-filter">
        <button
          className={`action-chip action-chip--all ${activeFilter === null ? "action-chip--active" : ""}`}
          onClick={() => setActiveFilter(null)}
        >
          <span className="action-chip__count">{decisions.length}</span>
          <span className="action-chip__label">all</span>
        </button>
        {presentActions.map((a) => (
          <button
            key={a}
            className={`action-chip action-chip--${a} ${activeFilter === a ? "action-chip--active" : ""}`}
            onClick={() => setActiveFilter((prev) => (prev === a ? null : a))}
          >
            <span className="action-chip__count">{counts[a]}</span>
            <span className="action-chip__label">{a}</span>
          </button>
        ))}
      </div>
      <table className="table">
        <thead>
          <tr>
            <SortableTh label="Package" k="package" sortKey={sortKey} sortDir={sortDir} onClick={clickHeader} />
            <SortableTh label="Versions" k="versions" sortKey={sortKey} sortDir={sortDir} onClick={clickHeader} />
            <SortableTh label="Action" k="action" sortKey={sortKey} sortDir={sortDir} onClick={clickHeader} />
            <SortableTh label="Why" k="why" sortKey={sortKey} sortDir={sortDir} onClick={clickHeader} />
            <th>Signals</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((d) => {
            const isHit = rowMatches(
              query,
              d.name, d.ecosystem, `${d.ecosystem}:${d.name}`, d.reason,
              ...d.affectedVersions, ...d.sources, ...d.sourceAdvisoryIds,
            );
            return (
              <tr key={d.auditEntryId ?? `${d.ecosystem}-${d.name}-${d.timestamp}`} className={isHit ? "row--match" : ""}>
                <td><code><Mark text={`${d.ecosystem}:${d.name}`} query={query} /></code></td>
                <td><code className="versions"><Mark text={d.affectedVersions.join(", ") || "*"} query={query} /></code></td>
                <td>
                  <span className={`action action--${d.action}`}>{d.action}</span>
                  {d.executed && <span className="executed-tag">executed</span>}
                </td>
                <td className="why"><Mark text={d.reason} query={query} /></td>
                <td className="signals"><DecisionSignals d={d} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function SortableTh({
  label, k, sortKey, sortDir, onClick,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey | null;
  sortDir: SortDir;
  onClick: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  const arrow = !active ? "" : sortDir === "asc" ? " ▲" : sortDir === "desc" ? " ▼" : "";
  return (
    <th className={`th-sort ${active ? "th-sort--active" : ""}`} onClick={() => onClick(k)}>
      {label}{arrow}
    </th>
  );
}

function sortValue(d: Decision, k: SortKey): string {
  switch (k) {
    case "package": return `${d.ecosystem}:${d.name}`;
    case "versions": return d.affectedVersions.join(",");
    case "action": return d.action;
    case "why": return d.reason;
    case "source": return d.sourceItemUrl || d.sourceAdvisoryIds[0] || d.sources.join(",");
  }
}

function DecisionSignals({ d }: { d: Decision }) {
  const bits: { key: string; text: string; cls: string }[] = [];
  if (d.cachedVersions.length > 0) {
    bits.push({ key: "ca", text: `CA: ${d.cachedVersions.length} ver in ${d.repos.join(",")}`, cls: "sig sig--ca" });
  }
  if (d.repos.length > 0 && d.cachedVersions.length === 0) {
    bits.push({ key: "ca", text: `CA: in cache (no compromised ver)`, cls: "sig sig--ca-clean" });
  }
  if (d.inEndor) bits.push({ key: "endor", text: `Endor: ${d.endorDependents.length} deps`, cls: "sig sig--endor" });
  if (d.internallyPublished) bits.push({ key: "internal", text: "internal", cls: "sig sig--internal" });
  if (d.weeklyDownloads !== null && d.weeklyDownloads !== undefined) {
    bits.push({ key: "dl", text: `${d.weeklyDownloads}/wk`, cls: "sig sig--dl" });
  }
  if (d.cloudtrailPulls.length > 0) {
    bits.push({
      key: "ct",
      text: `🚨 ${d.cloudtrailPulls.length} pull${d.cloudtrailPulls.length === 1 ? "" : "s"}`,
      cls: "sig sig--ct",
    });
  }
  if (d.caError) bits.push({ key: "cae", text: "CA err", cls: "sig sig--err" });
  if (d.endorError) bits.push({ key: "ee", text: "Endor err", cls: "sig sig--err" });
  if (d.executeError) bits.push({ key: "xe", text: "exec err", cls: "sig sig--err" });
  if (bits.length === 0) return <span className="signals--none">—</span>;
  return <>{bits.map((b) => <span key={b.key} className={b.cls}>{b.text}</span>)}</>;
}

function rowMatches(query: string, ...values: (string | null | undefined)[]): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  for (const v of values) if (v && String(v).toLowerCase().includes(q)) return true;
  return false;
}

// For blog-post sources, surface per-package GitHub-lookup results inline
// underneath the table. (For other source kinds the github events are still
// per-run but not source-attributable.) Kept compact — full sample list
// available in the original RunDetail view if needed.
function BlogPostExtras({
  source, run, query,
}: { source: Source; run: RunSummary; query: string }) {
  const pkgKeys = useMemo(
    () => new Set(source.decisions.map((d) => `${d.ecosystem}:${d.name}`)),
    [source.decisions],
  );
  const ghEvents = useMemo(
    () => run.githubLookups.filter((e) => {
      const eco = e.data.packageEcosystem as string | undefined;
      const name = e.data.packageName as string | undefined;
      return eco && name && pkgKeys.has(`${eco}:${name}`);
    }),
    [run.githubLookups, pkgKeys],
  );
  if (ghEvents.length === 0) return null;
  return (
    <section className="section">
      <h3>GitHub code search ({ghEvents.length})</h3>
      <table className="table table--compact">
        <thead>
          <tr>
            <th>Package</th>
            <th>Repos</th>
            <th>Files</th>
            <th>Sample</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {ghEvents.map((e, i) => <GhRow key={i} e={e} query={query} />)}
        </tbody>
      </table>
    </section>
  );
}

function GhRow({ e, query }: { e: LogEventBase; query: string }) {
  const name = e.data.packageName as string | undefined;
  const eco = e.data.packageEcosystem as string | undefined;
  const repos = (e.data.githubRepoCount as number | undefined) ?? 0;
  const files = (e.data.githubFileCount as number | undefined) ?? 0;
  const sample = (e.data.githubReposSample as string[] | undefined) ?? [];
  const err = (e.data.githubError as string | null | undefined) ?? null;
  return (
    <tr>
      <td><code><Mark text={`${eco}:${name}`} query={query} /></code></td>
      <td className={repos > 0 ? "gh-hit" : "gh-zero"}>{repos}</td>
      <td className={files > 0 ? "gh-hit" : "gh-zero"}>{files}</td>
      <td className="gh-sample">{sample.slice(0, 3).map((r, j) => <code key={j}>{r}</code>)}</td>
      <td>{err ? <span className="sig sig--err">{err}</span> : ""}</td>
    </tr>
  );
}
